import { useAtomValue, useSetAtom } from "jotai";
import { Node, NodeProps } from "@xyflow/react";
import classNames from "classnames";
import { Icon, Link, Text } from "@zapier/design-system";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_COLOR } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  isViewOnlyModeAtom,
  floatingToolbarActiveButtonAtom,
} from "@zapier/canvas-state";

import { truncateUrl } from "../../../../utils/truncateUrl";
import { StepTitle } from "../../../StepTitle";
import { UnstyledButton } from "../../../UnstyledButton";
import { AssetNodeDeterminer } from "../AssetNodeSkeleton/AssetNodeDeterminer";
import { NotesButton } from "../AssetNodeSkeleton/NotesButton";

import styles from "./DefaultNode.module.css";

interface DefaultNodeContentProps {
  className: string;
  children?: React.ReactNode;
  isViewOnlyMode?: boolean;
  backgroundColor?: string;
}

export function DefaultNodeContentInner({
  className,
  children,
  ...props
}: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps) {
  const { data, id, selected, backgroundColor } = props;
  const { label, color = DEFAULT_NODE_COLOR, link } = data;
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);

  const hasNote = !isRemirrorJSONEmpty(data.notesJson);

  const dynamicStyles = {
    "--border-color": color,
    "--background-color": backgroundColor,
  };

  const canNodeGetAiSuggestion =
    !data.app && !data.projectId && !data.pageId && !data.chatbotId;

  const openFloatingLinkInput = () => {
    setActiveButton("link");
  };

  return (
    <div
      className={classNames(styles.wrapper, styles.wrapperNew, className)}
      style={dynamicStyles as React.CSSProperties}
    >
      {children}
      <StepTitle
        nodeId={id}
        label={label}
        isSelected={Boolean(selected)}
        canNodeGetAiSuggestion={canNodeGetAiSuggestion}
        isViewOnlyMode={isViewOnlyMode}
      />
      <div className={styles.flexRow}>
        {link && (
          <div className={styles.linkContainer}>
            <UnstyledButton
              onClick={openFloatingLinkInput}
              disabled={isViewOnlyMode}
            >
              <Text color="TextWeakest" type="MinimalPrint1">
                {truncateUrl(link, 20)}
              </Text>
            </UnstyledButton>
            <Link href={link} target="_blank" className={styles.offsiteLink}>
              <Icon name="arrowOffsite" size={15} color="GrayWarm6" />
            </Link>
          </div>
        )}
        <NotesButton
          id={id}
          hasNotes={hasNote}
          isViewOnlyMode={isViewOnlyMode}
        />
      </div>
    </div>
  );
}

/* This is a presentational component - please keep all RF smarts in the outer DefaultNode component
 * We use this to render a node outside of RF.
 * */
export function DefaultNodeContent(
  props: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps,
) {
  return <AssetNodeDeterminer {...props} />;
}
